import { EditorContent, useEditor } from '@tiptap/react'; 
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Typography from '@tiptap/extension-typography';
import { Markdown } from 'tiptap-markdown';
import { useEffect, useState } from 'react';

const RichTextEditor = ({ content, onChange, placeholder }) => {
  const [isRawMode, setIsRawMode] = useState(false);
  const [rawText, setRawText] = useState('');
  const [showHotkeys, setShowHotkeys] = useState(false);

  const editor = useEditor({
    extensions:[
      StarterKit, 
      Link.configure({ openOnClick:false }), 
      Placeholder.configure({ placeholder }),
      Typography,
      Markdown
    ],
    editorProps: {
      attributes: {
        class: 'outline-none min-h-32 prose max-w-none prose-p:leading-[1.2] prose-li:leading-[1.2]'
      }
    },
    content: content || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    immediatelyRender: false
  })

  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
      if (isRawMode) {
        setRawText(editor.storage.markdown.getMarkdown());
      }
    }
  }, [content, editor, isRawMode]);

  if (!editor) {
    return null;
  }

  const toggleMode = () => {
    if (!isRawMode) {
      setRawText(editor.storage.markdown.getMarkdown());
    } else {
      editor.commands.setContent(rawText);
      onChange(editor.getHTML());
    }
    setIsRawMode(!isRawMode);
  };

  const handleRawChange = (e) => {
    const val = e.target.value;
    setRawText(val);
    editor.commands.setContent(val);
    onChange(editor.getHTML());
  };
  
  const Hotkey = ({ children }) => (
    <span className="px-1 py-0.5 bg-gray-100 rounded font-mono text-xs">
      {children}
    </span>
  );

  return (
    <div className="border bg-white rounded-lg flex flex-col focus-within:ring-2 focus-within:ring-blue-500 overflow-hidden">
      <style>{`
        .tiptap p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          float: left;
          color: #9ca3af;
          pointer-events: none;
          height: 0;
        }
      `}</style>

      <div className="px-4 py-2 text-xs text-gray-400 border-b bg-gray-50 flex justify-between items-center">
        <div className="space-x-2">
          <button
            type="button"
            onClick={toggleMode}
            className="text-xs px-2 py-1 bg-gray-200 rounded text-black hover:bg-blue-100 hover:border-blue-500 border-2 transition-colors"
          >
            {isRawMode ? 'Rich text mode' : 'Markdown mode'}
          </button>
          <span>Current mode: {isRawMode ? 'markdown' : 'rich text'}</span>
        </div>
        {!isRawMode && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowHotkeys(!showHotkeys)}
              className="hover:text-gray-600 cursor-pointer"
            >
              Hotkeys are supported
            </button>
          {showHotkeys && (
            <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg p-4 z-10 w-[360px] text-gray-700">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-semibold text-sm">Keyboard Shortcuts</h3>
                <button
                  onClick={() => setShowHotkeys(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  Close
                </button>
              </div>
              <div className="space-y-2 text-xs">
                <div className="grid grid-cols-2 gap-2">
                  <div><Hotkey>Ctrl+B</Hotkey> Bold</div>
                  <div><Hotkey>Ctrl+I</Hotkey> Italic</div>
                  <div><Hotkey>Ctrl+Shift+S</Hotkey> Strike</div>
                  <div><Hotkey>Ctrl+Alt+1-6</Hotkey> Heading</div>
                  <div><Hotkey>Ctrl+Shift+7</Hotkey> Ordered list</div>
                  <div><Hotkey>Ctrl+Shift+8</Hotkey> Bullet list</div>
                  <div><Hotkey>Ctrl+Shift+B</Hotkey> Quote</div>
                  <div><Hotkey>Ctrl+Z</Hotkey> Undo</div>
                  <div><Hotkey>Ctrl+Shift+Z</Hotkey> Redo</div>
                </div>
              </div>
            </div>
          )}
          </div>
        )}
      </div>

      <div className="relative">
        {isRawMode ? (
          <textarea
            value={rawText}
            onChange={handleRawChange}
            placeholder={placeholder || 'Enter text in markdown format (e.g., **bold** or # heading)'}
            className="w-full px-4 pt-3 min-h-[12rem] outline-none resize-y font-mono text-sm text-gray-800 bg-gray-50/30 leading-[1.2]"
          />
        ) : (
          <EditorContent 
            editor={editor} 
            className="max-w-none px-4 pt-3 min-h-[12rem] focus-within:outline-none"
          />
        )}
      </div>
    </div>
  )
}

export default RichTextEditor;